/**
 * This function manages the height of a frame and ensures that it is at least as tall as the content inside of it.
 * It will also make sure the content is at least as tall as the frame container.
 *
 * @method syncFrameToContentDimensions
 * @param {Element} frame - the iframe element that will have its height managed
 * @param {Number}  fontSize - current font size (scale by which transformation is taking place)
 * @param {Object} document - the current document of the book being rendered
 * @returns {Object} object containing the new width and height
 */
const syncFrameToContentDimensions = (frame, fontSize, document) => {
  if (!frame?.contentDocument?.body) {
    return;
  }

  const frameDocument = frame.contentDocument;
  const frameParent = frame.parentElement;

  const fixedWidth = document?.fixedWidth;
  const fixedHeight = document?.fixedHeight;

  const width = Math.max(
    frameDocument.documentElement.offsetWidth,
    frameDocument.body.offsetWidth,
  );

  let height = Math.max(
    frameDocument.documentElement.offsetHeight,
    frameDocument.body.offsetHeight,
    frameParent.offsetHeight, // do not let the iframe be shorter than its container
  );

  if (fixedWidth) {
    const fixedWidthScaled = fixedWidth * fontSize;
    // Only set a width on the iframe if there is a fixed width.
    frame.style.width = `${fixedWidthScaled}px`;
  }

  if (fixedHeight) {
    /**
     * If the content has a fixed height metadata property set, ensure that the container is no shorter than it.
     *
     * Fixed height may be useful for a document consisting of an image with no set dimensions that needs a certain amount of height,
     * e.g. a non-ocr'd PDF.
     * But if a publisher sets a fixed height for consistency because they set a fixed width,
     * it should not constrain otherwise free-flowing content so that it is cut out of view.
     */
    const heightToScale = Math.max(height, fixedHeight);

    height = heightToScale * fontSize;
  }

  frame.style.height = `${height}px`;

  return { width, height };
};

export default syncFrameToContentDimensions;
